/**
 * NoteAutosaveWrapper - Provides autosave functionality for note forms
 * 
 * This component wraps note editing/creation forms and provides:
 * - Silent autosave every 45 seconds + on field blur
 * - Encrypted sessionStorage for offline draft recovery
 * - Visual save indicator
 * - Draft recovery modal
 * 
 * Usage:
 * <NoteAutosaveWrapper
 *   noteId={selectedNote?.id}
 *   formData={newNoteFormData}
 *   enabled={isEditingNote || showAddNote}
 *   onFormDataRestore={(data) => setNewNoteFormData(data)}
 * >
 *   {({ onFieldBlur }) => (
 *     <YourFormFields onBlur={onFieldBlur} />
 *   )}
 * </NoteAutosaveWrapper>
 */

import React, { useCallback, useMemo } from 'react';
import { useNoteAutosave, SaveStatus } from '../hooks/useNoteAutosave';
import { SaveIndicator, FloatingSaveIndicator } from './SaveIndicator';
import { DraftRecoveryModal, DraftRecoveryBanner } from './DraftRecoveryModal';

/**
 * NoteAutosaveWrapper Component
 */
export function NoteAutosaveWrapper({
  noteId,
  formData,
  enabled = true,
  onFormDataRestore,
  children,
  showFloatingIndicator = true,
  showBanner = true,
  indicatorPosition = 'top-right',
}) {
  // Get auth token - use sessionStorage (app's token storage)
  const getAuthToken = useCallback(() => {
    return sessionStorage.getItem('token');
  }, []);

  // Initialize autosave hook
  const {
    onFieldBlur,
    forceSave,
    restoreFromDraft,
    dismissDraftRecovery,
    saveStatus,
    lastSavedAt,
    isSaving,
    showDraftRecovery,
    recoveredDraft,
    clearLocalDraft,
    sessionId,
  } = useNoteAutosave({
    noteId,
    formData,
    enabled: enabled && !!noteId,
    getAuthToken,
    onFormDataRestore,
  });

  // Memoize the context value to prevent unnecessary re-renders
  const autosaveContext = useMemo(() => ({
    onFieldBlur,
    forceSave,
    saveStatus,
    lastSavedAt,
    isSaving,
    clearLocalDraft,
    sessionId,
  }), [onFieldBlur, forceSave, saveStatus, lastSavedAt, isSaving, clearLocalDraft, sessionId]);

  return (
    <div className="relative">
      {/* Draft Recovery Banner */}
      {showBanner && (
        <DraftRecoveryBanner
          isVisible={showDraftRecovery}
          onRestore={restoreFromDraft}
          onDismiss={dismissDraftRecovery}
          draft={recoveredDraft}
        />
      )}

      {/* Draft Recovery Modal (alternative to banner) */}
      {!showBanner && (
        <DraftRecoveryModal
          isOpen={showDraftRecovery}
          onRestore={restoreFromDraft}
          onDismiss={dismissDraftRecovery}
          draft={recoveredDraft}
        />
      )}

      {/* Floating Save Indicator */}
      {showFloatingIndicator && enabled && noteId && (
        <FloatingSaveIndicator
          status={saveStatus}
          lastSavedAt={lastSavedAt}
          position={indicatorPosition}
        />
      )}

      {/* Render children with autosave context */}
      {typeof children === 'function' 
        ? children(autosaveContext)
        : children
      }
    </div>
  );
}

/**
 * Inline Save Indicator for use in headers/footers
 * 
 * Usage:
 * <NoteAutosaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />
 */
export function NoteAutosaveIndicator({ status, lastSavedAt, className = '' }) {
  // Don't show if idle and never saved
  if (status === SaveStatus.IDLE && !lastSavedAt) {
    return null;
  }

  return (
    <SaveIndicator
      status={status}
      lastSavedAt={lastSavedAt}
      className={className}
      showTimestamp={true}
    />
  );
}

/**
 * Hook for using autosave in existing components
 * 
 * This is a simpler alternative to the wrapper component.
 * Use this when you need more control over the UI.
 * 
 * Now captures FULL form state: content, consent, and treatment plan goals.
 * 
 * Usage:
 * const { onFieldBlur, SaveIndicatorComponent, DraftRecoveryComponent } = useNoteAutosaveUI({
 *   noteId: selectedNote?.id,
 *   formData: editedNoteContent,
 *   consentData: editedNoteConsent,      // NEW: Telehealth consent
 *   linkedGoals: linkedGoals,             // NEW: Treatment plan goals
 *   enabled: isEditingNote,
 *   onFormDataRestore: (restoredState) => {
 *     setEditedNoteContent(restoredState.formData);
 *     setEditedNoteConsent(restoredState.consentData);
 *     setLinkedGoals(restoredState.linkedGoals);
 *   },
 * });
 */
export function useNoteAutosaveUI({
  noteId,
  setNoteId,               // NEW: Callback to update noteId after lazy creation
  formData,
  consentData = null,      // NEW: Telehealth consent selections
  linkedGoals = [],        // NEW: Treatment plan goals
  metadata = null,         // NEW: Note metadata (date_of_service, times, billing info)
  enabled = true,
  onFormDataRestore,
  initialVersion = null,   // Initial version from server when note was loaded
  serverUpdatedAt = null,  // Server's updated_at for timestamp comparison
  onVersionConflict,       // Callback when optimistic lock fails
  createNoteData = null,   // NEW: Data for lazy auto-creation
  onNoteCreated = null,    // NEW: Callback after lazy creation succeeds
  // NEW: Loud failure handlers
  onSaveError = null,      // Callback to set autosaveBlockedReason
  onSaveCriticalError = null, // Callback to set noteCreationError (shows banner)
  // NEW: Client/Provider names for recovery banner display
  clientName = null,       // Shows "Found changes for [Client Name]" in recovery
  providerName = null,     // Additional context in recovery prompt
  // NEW: Client ID for cross-contamination prevention
  clientId = null,         // Client ID to verify against note's client_id on every save
  // Jun 2026: empty-note auto-restore control (see useNoteAutosave)
  noteEmptyManuallySaved = false,
}) {
  // Get auth token - use sessionStorage (app's token storage)
  const getAuthToken = useCallback(() => {
    return sessionStorage.getItem('token');
  }, []);

  const finalEnabled = enabled && (!!noteId || !!createNoteData);
  
  const autosave = useNoteAutosave({
    noteId,
    setNoteId,             // Pass setNoteId for lazy creation
    formData,
    consentData,           // Pass consent data
    linkedGoals,           // Pass linked goals
    metadata,              // Pass metadata (date_of_service, times, etc.)
    // UPDATED: Enable when noteId exists OR createNoteData is available (for lazy creation)
    enabled: finalEnabled,
    getAuthToken,
    onFormDataRestore,
    initialVersion,        // Pass initial version for optimistic locking
    serverUpdatedAt,       // Pass server timestamp for comparison
    onVersionConflict,     // Pass conflict callback
    createNoteData,        // Pass createNoteData for lazy creation
    onNoteCreated,         // Pass callback for lazy creation success
    // NEW: Pass loud failure handlers
    onSaveError,
    onSaveCriticalError,
    // NEW: Pass client/provider names for recovery display
    clientName,
    providerName,
    // NEW: Pass clientId for cross-contamination prevention
    clientId,
    noteEmptyManuallySaved,
  });

  // Pre-configured UI components
  const SaveIndicatorComponent = useCallback(({ className } = {}) => (
    <SaveIndicator
      status={autosave.saveStatus}
      lastSavedAt={autosave.lastSavedAt}
      className={className}
    />
  ), [autosave.saveStatus, autosave.lastSavedAt]);

  const FloatingSaveIndicatorComponent = useCallback(({ position = 'top-right' } = {}) => (
    <FloatingSaveIndicator
      status={autosave.saveStatus}
      lastSavedAt={autosave.lastSavedAt}
      position={position}
    />
  ), [autosave.saveStatus, autosave.lastSavedAt]);

  const DraftRecoveryBannerComponent = useCallback(() => (
    <DraftRecoveryBanner
      isVisible={autosave.showDraftRecovery}
      onRestore={autosave.restoreFromDraft}
      onDismiss={autosave.dismissDraftRecovery}
      draft={autosave.recoveredDraft}
    />
  ), [autosave.showDraftRecovery, autosave.restoreFromDraft, autosave.dismissDraftRecovery, autosave.recoveredDraft]);

  const DraftRecoveryModalComponent = useCallback(() => (
    <DraftRecoveryModal
      isOpen={autosave.showDraftRecovery}
      onRestore={autosave.restoreFromDraft}
      onDismiss={autosave.dismissDraftRecovery}
      draft={autosave.recoveredDraft}
    />
  ), [autosave.showDraftRecovery, autosave.restoreFromDraft, autosave.dismissDraftRecovery, autosave.recoveredDraft]);

  return {
    ...autosave,
    SaveIndicatorComponent,
    FloatingSaveIndicatorComponent,
    DraftRecoveryBannerComponent,
    DraftRecoveryModalComponent,
  };
}

export default NoteAutosaveWrapper;
